import React, { useState, useEffect } from 'react';
import { listenToRides, assignDriver, updateRideStatus, addStopToRide, getDrivers, createRide, getCustomers } from '../firebase/database';
import { Modal } from './DriversPage';

const STATUS_TABS = ['all', 'pending', 'assigned', 'accepted', 'in_progress', 'completed', 'cancelled'];

const STATUS_COLORS = {
  pending:     { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
  assigned:    { bg: '#dbeafe', color: '#1e40af', label: 'Assigned' },
  accepted:    { bg: '#dcfce7', color: '#15803d', label: 'Accepted' },
  in_progress: { bg: '#e0e7ff', color: '#4338ca', label: 'In Progress' },
  completed:   { bg: '#f0fdf4', color: '#166534', label: 'Completed' },
  cancelled:   { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
};

export default function RidesPage() {
  const [rides, setRides]         = useState([]);
  const [drivers, setDrivers]     = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tab, setTab]             = useState('all');
  const [selectedRide, setSelectedRide]   = useState(null);
  const [showAssign, setShowAssign]       = useState(false);
  const [showStops, setShowStops]         = useState(false);
  const [showDetail, setShowDetail]       = useState(false);
  const [showCreateRide, setShowCreateRide] = useState(false);

  useEffect(() => {
    const unsub = listenToRides(setRides);
    getDrivers().then(setDrivers);
    getCustomers().then(setCustomers);
    return unsub;
  }, []);

  const filtered = tab === 'all' ? rides : rides.filter(r => r.status === tab);
  const pendingCount = rides.filter(r => r.status === 'pending').length;

  return (
    <div>
      {/* Header row with Create Ride button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button
          onClick={() => setShowCreateRide(true)}
          style={{
            padding: '9px 18px', background: '#f59e0b', border: 'none',
            borderRadius: 8, color: '#0f172a', fontWeight: 700, fontSize: 14,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          🚗 + Create Ride
        </button>
      </div>

      {/* Status Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', padding: '4px 0' }}>
        {STATUS_TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '7px 14px', borderRadius: 8, border: 'none',
            background: tab === t ? '#0f172a' : '#f1f5f9',
            color: tab === t ? '#fff' : '#475569',
            fontSize: 13, fontWeight: tab === t ? 700 : 400,
            cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            textTransform: 'capitalize'
          }}>
            {t.replace('_', ' ')}
            {t === 'pending' && pendingCount > 0 && (
              <span style={{
                marginLeft: 6, background: '#ef4444', color: '#fff',
                borderRadius: 10, padding: '1px 6px', fontSize: 11
              }}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Rides List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '48px 20px', textAlign: 'center', color: '#94a3b8' }}>
            No rides in this category
          </div>
        )}
        {filtered.map(ride => (
          <RideCard
            key={ride.id}
            ride={ride}
            drivers={drivers}
            onAssign={() => { setSelectedRide(ride); setShowAssign(true); }}
            onManageStops={() => { setSelectedRide(ride); setShowStops(true); }}
            onViewDetail={() => { setSelectedRide(ride); setShowDetail(true); }}
            onCancel={() => updateRideStatus(ride.id, 'cancelled')}
          />
        ))}
      </div>

      {showAssign && selectedRide && (
        <AssignDriverModal ride={selectedRide} drivers={drivers} onClose={() => setShowAssign(false)} />
      )}
      {showStops && selectedRide && (
        <ManageStopsModal ride={selectedRide} onClose={() => setShowStops(false)} />
      )}
      {showDetail && selectedRide && (
        <RideDetailModal ride={selectedRide} onClose={() => setShowDetail(false)} />
      )}
      {showCreateRide && (
        <CreateRideModal
          customers={customers}
          drivers={drivers}
          onClose={() => setShowCreateRide(false)}
        />
      )}
    </div>
  );
}

// ── Ride Card ──────────────────────────────────────────────
function RideCard({ ride, drivers, onAssign, onManageStops, onViewDetail, onCancel }) {
  const sc = STATUS_COLORS[ride.status] || { bg: '#f1f5f9', color: '#475569', label: ride.status };
  const dt = ride.pickupDateTime?.toDate ? ride.pickupDateTime.toDate() : new Date(ride.pickupDateTime);

  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
      padding: 20, display: 'flex', gap: 20, flexWrap: 'wrap'
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%', background: '#fef3c7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 15, color: '#92400e'
          }}>
            {ride.customerName?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{ride.customerName}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{ride.customerPhone}</div>
          </div>
          <span style={{
            marginLeft: 'auto', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: sc.bg, color: sc.color
          }}>{sc.label}</span>
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
          📍 <strong>Pickup:</strong> {ride.pickupAddress}
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
          🕒 <strong>Date/Time:</strong> {!isNaN(dt) ? dt.toLocaleString('en-US') : 'N/A'}
        </div>
        {ride.stops?.length > 0 && (
          <div style={{ fontSize: 13, color: '#475569' }}>
            🚩 <strong>Stops:</strong> {ride.stops.length} stop{ride.stops.length !== 1 ? 's' : ''}
          </div>
        )}
        {ride.driverName && (
          <div style={{ fontSize: 13, color: '#1d4ed8', marginTop: 4 }}>
            🚗 <strong>Driver:</strong> {ride.driverName}
          </div>
        )}
        {ride.adminCreated && (
          <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>🔧 Admin created ride</div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center', flexShrink: 0 }}>
        <button onClick={onViewDetail} style={btnStyle('#6366f1')}>View Details</button>
        {['pending', 'assigned'].includes(ride.status) && (
          <button onClick={onAssign} style={btnStyle('#f59e0b')}>
            {ride.driverId ? 'Reassign Driver' : 'Assign Driver'}
          </button>
        )}
        {['assigned', 'accepted', 'in_progress'].includes(ride.status) && (
          <button onClick={onManageStops} style={btnStyle('#22c55e')}>Manage Stops</button>
        )}
        {['pending', 'assigned'].includes(ride.status) && (
          <button onClick={onCancel} style={btnStyle('#ef4444')}>Cancel Ride</button>
        )}
      </div>
    </div>
  );
}

function btnStyle(color) {
  return {
    padding: '8px 14px', background: `${color}18`, border: `1px solid ${color}40`,
    borderRadius: 8, color, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
  };
}

// ── Create Ride Modal (Admin) ──────────────────────────────
function CreateRideModal({ customers, drivers, onClose }) {
  const [step, setStep]             = useState(1); // 1=customer, 2=ride details, 3=assign driver
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch]     = useState('');
  const [pickupAddress, setPickupAddress]       = useState('');
  const [pickupDate, setPickupDate]             = useState('');
  const [pickupTime, setPickupTime]             = useState('');
  const [adminNotes, setAdminNotes]             = useState('');
  const [selectedDriver, setSelectedDriver]     = useState('');
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState('');

  const approvedCustomers = customers.filter(c => c.status === 'approved');
  const filteredCustomers = approvedCustomers.filter(c =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch)
  );

  const handleCreateAndAssign = async () => {
    if (!pickupAddress.trim() || !pickupDate || !pickupTime) {
      setError('Pickup address, date and time are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const pickupDateTime = new Date(`${pickupDate}T${pickupTime}`);
      const driver = drivers.find(d => d.id === selectedDriver);

      const rideData = {
        customerId:    selectedCustomer.id,
        customerName:  selectedCustomer.name,
        customerPhone: selectedCustomer.phone || '',
        pickupAddress: pickupAddress.trim(),
        pickupLatitude:  null,
        pickupLongitude: null,
        pickupDateTime,
        adminNotes:    adminNotes.trim(),
        adminCreated:  true,
        stops: [],
      };

      const ref = await createRide(rideData);

      // If driver selected, assign immediately
      if (selectedDriver && driver) {
        await assignDriver(ref.id, selectedDriver, driver.name);
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create ride.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create Ride" onClose={onClose}>
      {/* Step indicators */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24 }}>
        {['Select Customer', 'Ride Details', 'Assign Driver'].map((label, i) => {
          const stepNum = i + 1;
          const active  = step === stepNum;
          const done    = step > stepNum;
          return (
            <div key={label} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {i > 0 && <div style={{ flex: 1, height: 2, background: done ? '#f59e0b' : '#e2e8f0' }} />}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: active ? '#f59e0b' : done ? '#22c55e' : '#e2e8f0',
                  color: active || done ? '#fff' : '#94a3b8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, margin: '0 auto'
                }}>{done ? '✓' : stepNum}</div>
                {i < 2 && <div style={{ flex: 1, height: 2, background: done ? '#f59e0b' : '#e2e8f0' }} />}
              </div>
              <div style={{ fontSize: 10, color: active ? '#f59e0b' : '#94a3b8', marginTop: 4, fontWeight: active ? 600 : 400 }}>{label}</div>
            </div>
          );
        })}
      </div>

      {/* Step 1: Select Customer */}
      {step === 1 && (
        <div>
          <input
            placeholder="Search customer by name or phone..."
            value={customerSearch}
            onChange={e => setCustomerSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
          />
          <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredCustomers.length === 0 && (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24, fontSize: 13 }}>
                No approved customers found.<br />
                <a href="#/customers" onClick={onClose} style={{ color: '#3b82f6', fontSize: 13 }}>Go to Customers to create one →</a>
              </div>
            )}
            {filteredCustomers.map(c => (
              <div
                key={c.id}
                onClick={() => setSelectedCustomer(c)}
                style={{
                  padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${selectedCustomer?.id === c.id ? '#f59e0b' : '#e2e8f0'}`,
                  background: selectedCustomer?.id === c.id ? '#fffbeb' : '#fff',
                  display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.1s'
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: c.createdByAdmin ? '#dbeafe' : '#fef3c7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14, color: c.createdByAdmin ? '#1e40af' : '#92400e', flexShrink: 0
                }}>{c.name?.charAt(0)?.toUpperCase()}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{c.phone} {c.createdByAdmin ? '· 🔧 Admin Account' : ''}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              onClick={() => selectedCustomer && setStep(2)}
              disabled={!selectedCustomer}
              style={{
                padding: '10px 24px', background: selectedCustomer ? '#f59e0b' : '#e2e8f0',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                cursor: selectedCustomer ? 'pointer' : 'not-allowed',
                color: selectedCustomer ? '#0f172a' : '#94a3b8'
              }}
            >Next →</button>
          </div>
        </div>
      )}

      {/* Step 2: Ride Details */}
      {step === 2 && (
        <div>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#475569' }}>
            👤 Customer: <strong style={{ color: '#0f172a' }}>{selectedCustomer?.name}</strong> · {selectedCustomer?.phone}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>Pickup Address *</label>
            <input
              value={pickupAddress}
              onChange={e => setPickupAddress(e.target.value)}
              placeholder="Full pickup address in Chicago..."
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>Pickup Date *</label>
              <input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>Pickup Time *</label>
              <input type="time" value={pickupTime} onChange={e => setPickupTime(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>Admin Notes</label>
            <textarea
              value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="Any special instructions..."
              rows={3}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
            <button onClick={() => setStep(1)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}>← Back</button>
            <button onClick={() => {
              if (!pickupAddress.trim() || !pickupDate || !pickupTime) { setError('All fields marked * are required.'); return; }
              setError(''); setStep(3);
            }} style={{ padding: '10px 24px', background: '#f59e0b', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Next →</button>
          </div>
        </div>
      )}

      {/* Step 3: Assign Driver */}
      {step === 3 && (
        <div>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#475569' }}>
            👤 <strong style={{ color: '#0f172a' }}>{selectedCustomer?.name}</strong> · 📍 {pickupAddress}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>
            Assign a Driver <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>(optional — can assign later)</span>
          </div>

          {/* No driver option */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
            borderRadius: 8, border: `2px solid ${selectedDriver === '' ? '#f59e0b' : '#e2e8f0'}`,
            background: selectedDriver === '' ? '#fffbeb' : '#fff', cursor: 'pointer', marginBottom: 8
          }}>
            <input type="radio" name="driver" checked={selectedDriver === ''} onChange={() => setSelectedDriver('')} style={{ accentColor: '#f59e0b' }} />
            <div style={{ fontSize: 14, color: '#475569', fontWeight: 500 }}>⏳ Leave pending — assign later</div>
          </label>

          {drivers.filter(d => d.isActive).map(driver => (
            <label key={driver.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              borderRadius: 8, border: `2px solid ${selectedDriver === driver.id ? '#f59e0b' : '#e2e8f0'}`,
              background: selectedDriver === driver.id ? '#fffbeb' : '#fff', cursor: 'pointer', marginBottom: 8, transition: 'all 0.1s'
            }}>
              <input type="radio" name="driver" checked={selectedDriver === driver.id} onChange={() => setSelectedDriver(driver.id)} style={{ accentColor: '#f59e0b' }} />
              <div style={{
                width: 34, height: 34, borderRadius: '50%', background: '#dbeafe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 13, color: '#1e40af', flexShrink: 0
              }}>{driver.name?.charAt(0)?.toUpperCase()}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{driver.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{driver.vehicleType} · {driver.vehicleNumber} · {driver.phone}</div>
              </div>
            </label>
          ))}

          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1e40af', marginBottom: 16 }}>
            ℹ️ The driver will receive a push notification once assigned. Customer will be notified of their driver and pickup time.
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
            <button onClick={() => setStep(2)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}>← Back</button>
            <button onClick={handleCreateAndAssign} disabled={saving} style={{
              padding: '10px 24px', background: '#22c55e', border: 'none',
              borderRadius: 8, fontWeight: 700, fontSize: 14, color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
            }}>
              {saving ? 'Creating...' : selectedDriver ? '✓ Create & Assign Ride' : '✓ Create Ride'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Assign Driver Modal ────────────────────────────────────
function AssignDriverModal({ ride, drivers, onClose }) {
  const [selected, setSelected] = useState(ride.driverId || '');
  const [saving, setSaving]     = useState(false);

  const handleAssign = async () => {
    if (!selected) return;
    setSaving(true);
    const driver = drivers.find(d => d.id === selected);
    await assignDriver(ride.id, selected, driver.name);
    setSaving(false);
    onClose();
  };

  return (
    <Modal title="Assign Driver to Ride" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
          Customer: <strong>{ride.customerName}</strong> — {ride.pickupAddress}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>Select a Driver:</div>
        {drivers.filter(d => d.isActive).map(driver => (
          <label key={driver.id} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8,
            border: `2px solid ${selected === driver.id ? '#f59e0b' : '#e2e8f0'}`,
            background: selected === driver.id ? '#fffbeb' : '#fff',
            cursor: 'pointer', marginBottom: 8, transition: 'all 0.15s'
          }}>
            <input type="radio" name="driver" checked={selected === driver.id} onChange={() => setSelected(driver.id)} style={{ accentColor: '#f59e0b' }} />
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#1e40af' }}>
              {driver.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{driver.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{driver.vehicleType} · {driver.vehicleNumber} · {driver.phone}</div>
            </div>
          </label>
        ))}
        {drivers.filter(d => d.isActive).length === 0 && (
          <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>No active drivers available</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleAssign} disabled={!selected || saving} style={{
          padding: '10px 20px', background: '#f59e0b', border: 'none',
          borderRadius: 8, fontWeight: 700, cursor: selected && !saving ? 'pointer' : 'not-allowed', fontSize: 14
        }}>
          {saving ? 'Assigning...' : 'Confirm Assignment'}
        </button>
      </div>
    </Modal>
  );
}

// ── Manage Stops Modal ────────────────────────────────────
function ManageStopsModal({ ride, onClose }) {
  const [stops, setStops]   = useState(ride.stops || []);
  const [newStop, setNewStop] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddStop = async () => {
    if (!newStop.trim()) return;
    setSaving(true);
    await addStopToRide(ride.id, { address: newStop.trim(), latitude: null, longitude: null });
    setStops(prev => [...prev, { address: newStop.trim(), completed: false, order: prev.length }]);
    setNewStop('');
    setSaving(false);
  };

  return (
    <Modal title="Manage Stops" onClose={onClose}>
      <div style={{ marginBottom: 16, fontSize: 13, color: '#64748b' }}>Ride for <strong>{ride.customerName}</strong></div>
      <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8 }}>
        📍 <strong>Pickup:</strong> {ride.pickupAddress}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Current Stops ({stops.length})</div>
        {stops.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>No stops added yet</div>
        ) : (
          stops.map((stop, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', background: stop.completed ? '#f0fdf4' : '#f8fafc',
              borderRadius: 8, marginBottom: 6, border: '1px solid #e2e8f0'
            }}>
              <span style={{ fontSize: 16 }}>{stop.completed ? '✅' : `${i + 1}.`}</span>
              <div style={{ flex: 1, fontSize: 13, color: '#374151' }}>{stop.address}</div>
              {stop.completed && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>Done</span>}
            </div>
          ))
        )}
      </div>
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Add New Stop</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={newStop}
            onChange={e => setNewStop(e.target.value)}
            placeholder="Enter stop address..."
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none' }}
            onKeyDown={e => e.key === 'Enter' && handleAddStop()}
          />
          <button onClick={handleAddStop} disabled={saving || !newStop.trim()} style={{
            padding: '10px 16px', background: '#22c55e', border: 'none',
            borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14
          }}>
            {saving ? '...' : '+ Add'}
          </button>
        </div>
      </div>
      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '10px 20px', background: '#0f172a', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Done</button>
      </div>
    </Modal>
  );
}

// ── Ride Detail Modal ──────────────────────────────────────
function RideDetailModal({ ride, onClose }) {
  const dt = ride.pickupDateTime?.toDate ? ride.pickupDateTime.toDate() : new Date(ride.pickupDateTime);
  return (
    <Modal title="Ride Details" onClose={onClose}>
      <Detail label="Customer"       value={ride.customerName} />
      <Detail label="Phone"          value={ride.customerPhone} />
      <Detail label="Pickup"         value={ride.pickupAddress} />
      <Detail label="Date & Time"    value={!isNaN(dt) ? dt.toLocaleString('en-US') : 'N/A'} />
      <Detail label="Status"         value={ride.status?.replace('_', ' ')} capitalize />
      <Detail label="Assigned Driver" value={ride.driverName || 'Not assigned'} />
      {ride.stops?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>Stops:</div>
          {ride.stops.map((s, i) => (
            <div key={i} style={{ fontSize: 13, color: '#374151', padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
              {i + 1}. {s.address} {s.completed ? '✅' : ''}
            </div>
          ))}
        </div>
      )}
      {ride.adminNotes && <Detail label="Notes" value={ride.adminNotes} />}
    </Modal>
  );
}

function Detail({ label, value, capitalize }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 14 }}>
      <span style={{ color: '#64748b', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#0f172a', fontWeight: 600, textTransform: capitalize ? 'capitalize' : 'none' }}>{value}</span>
    </div>
  );
}
